import {
	faCheckCircle,
	faSpinner,
	faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	PortalRegisterRequestDto,
	PortalRegisterResponseDto,
	PublicRegistrationFieldDto,
	PublicStandardFieldDto,
} from '@supply-mover-2-0/shared';
import { US_STATES } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import {
	fetchRegistrationForm,
	submitRegistration,
} from '../services/portalRegisterService';

/**
 * `'modal'` strips outer padding so the LoginModal's own card chrome
 * shows through; `'page'` adds page-level spacing. Both surfaces share
 * 100% of the form logic via this component.
 */
type Variant = 'modal' | 'page';

interface RegistrationFormProps {
	/**
	 * Portal alias the lead is registering against. Required — the
	 * server validates this against the portal_site table and refuses
	 * submissions for unknown / disabled tenants.
	 */
	tenantAlias: string;
	/** Visual treatment — see Variant. */
	variant?: Variant;
	/** Called after the success screen, when the user clicks "Done". */
	onComplete?: () => void;
	/** Optional cancel button — only shown when provided (e.g. modal back arrow). */
	onCancel?: () => void;
}

interface FormState {
	companyName: string;
	contactFirstName: string;
	contactLastName: string;
	email: string;
	phone: string;
	address1: string;
	address2: string;
	city: string;
	state: string;
	zip: string;
	businessType: string;
	federalEin: string;
	leadSource: string;
	notes: string;
	/** Honeypot — kept hidden via CSS. Bots fill it; humans don't. */
	website: string;
}

/**
 * Map server-side field names (DTO keys) to client form keys. Almost
 * everything matches 1:1; only the company-name field uses different
 * naming on the wire vs in the form.
 */
function mapFieldErrors(
	serverErrors: Record<string, string>,
): Record<string, string> {
	const mapped: Record<string, string> = {};
	for (const [key, value] of Object.entries(serverErrors)) {
		if (key === 'name') {
			mapped.companyName = value;
		} else {
			mapped[key] = value;
		}
	}
	return mapped;
}

const EMPTY_FORM: FormState = {
	companyName: '',
	contactFirstName: '',
	contactLastName: '',
	email: '',
	phone: '',
	address1: '',
	address2: '',
	city: '',
	state: '',
	zip: '',
	businessType: '',
	federalEin: '',
	leadSource: '',
	notes: '',
	website: '',
};

/** Visibility + required state for one configurable standard field. */
interface StandardFieldState {
	isVisible: boolean;
	isRequired: boolean;
}

/**
 * Default standard-field config (PD-14370) used before the portal's config
 * loads and as a fallback if the best-effort fetch fails — it reproduces the
 * historical "everything shown, company + address required" form so the storefront
 * degrades gracefully. The server-resolved config overrides this per field.
 */
const DEFAULT_STANDARD_CONFIG: Record<string, StandardFieldState> = {
	companyName: { isVisible: true, isRequired: true },
	address1: { isVisible: true, isRequired: true },
	address2: { isVisible: true, isRequired: false },
	city: { isVisible: true, isRequired: true },
	state: { isVisible: true, isRequired: true },
	zip: { isVisible: true, isRequired: true },
	businessType: { isVisible: true, isRequired: false },
	leadSource: { isVisible: true, isRequired: false },
	notes: { isVisible: true, isRequired: false },
};

export function RegistrationForm({
	tenantAlias,
	variant = 'page',
	onComplete,
	onCancel,
}: RegistrationFormProps): JSX.Element {
	const { t } = useTranslation();
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [bannerError, setBannerError] = useState<string | null>(null);
	const [submittedResult, setSubmittedResult] =
		useState<PortalRegisterResponseDto | null>(null);
	// Admin-configured supplemental questions (PD-14331) + their answers.
	const [regFields, setRegFields] = useState<Array<PublicRegistrationFieldDto>>(
		[],
	);
	const [regAnswers, setRegAnswers] = useState<Record<string, unknown>>({});
	// Which built-in standard fields to show + require for this portal (PD-14370).
	const [standardConfig, setStandardConfig] = useState<
		Record<string, StandardFieldState>
	>(DEFAULT_STANDARD_CONFIG);

	const isModal = variant === 'modal';

	// Load the configured supplemental questions for this storefront so the form
	// can render them dynamically below the standard fields. Best-effort: on any
	// failure the form just shows the standard fields.
	useEffect(() => {
		let active = true;
		void (async () => {
			const result = await fetchRegistrationForm(tenantAlias);
			if (active && result.success) {
				setRegFields(result.fields);
				if (result.standardFields.length > 0) {
					const map: Record<string, StandardFieldState> = {
						...DEFAULT_STANDARD_CONFIG,
					};
					for (const field of result.standardFields as Array<PublicStandardFieldDto>) {
						// Coerce defensively: these map to MySQL tinyint(1) and can
						// arrive as 0/1 — a numeric 0 in a `{cond && <JSX/>}` guard would
						// render a literal "0" on the form.
						map[field.fieldKey] = {
							isVisible: Boolean(field.isVisible),
							isRequired: Boolean(field.isRequired),
						};
					}
					setStandardConfig(map);
				}
			}
		})();
		return () => {
			active = false;
		};
	}, [tenantAlias]);

	const handleChange =
		<K extends keyof FormState>(field: K) =>
		(
			e: React.ChangeEvent<
				HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
			>,
		): void => {
			setForm((prev) => ({ ...prev, [field]: e.target.value }));
			if (errors[field] != null) {
				setErrors((prev) => {
					const next = { ...prev };
					delete next[field];
					return next;
				});
			}
		};

	const handleRegChange = (fieldKey: string, value: unknown): void => {
		setRegAnswers((prev) => ({ ...prev, [fieldKey]: value }));
		if (errors[fieldKey] != null) {
			setErrors((prev) => {
				const next = { ...prev };
				delete next[fieldKey];
				return next;
			});
		}
	};

	const toggleMultiselect = (fieldKey: string, optionValue: string): void => {
		setRegAnswers((prev) => {
			const current = Array.isArray(prev[fieldKey])
				? (prev[fieldKey] as Array<string>)
				: [];
			const nextValues = current.includes(optionValue)
				? current.filter((entry) => entry !== optionValue)
				: [...current, optionValue];
			return { ...prev, [fieldKey]: nextValues };
		});
		if (errors[fieldKey] != null) {
			setErrors((prev) => {
				const next = { ...prev };
				delete next[fieldKey];
				return next;
			});
		}
	};

	const handleSubmit = async (e: React.FormEvent): Promise<void> => {
		e.preventDefault();
		if (isSubmitting) return;
		setIsSubmitting(true);
		setBannerError(null);
		setErrors({});

		const payload: PortalRegisterRequestDto = {
			tenantAlias,
			name: form.companyName.trim(),
			contactFirstName: form.contactFirstName.trim(),
			contactLastName: form.contactLastName.trim(),
			email: form.email.trim(),
			phone: form.phone.trim(),
			address1: form.address1.trim(),
			address2: form.address2.trim().length > 0 ? form.address2.trim() : null,
			city: form.city.trim(),
			state: form.state.trim(),
			zip: form.zip.trim(),
			businessType:
				form.businessType.trim().length > 0 ? form.businessType.trim() : null,
			federalEin:
				form.federalEin.trim().length > 0 ? form.federalEin.trim() : null,
			leadSource:
				form.leadSource.trim().length > 0 ? form.leadSource.trim() : null,
			notes: form.notes.trim().length > 0 ? form.notes.trim() : null,
			honeypot: form.website,
			registrationFields:
				Object.keys(regAnswers).length > 0 ? regAnswers : null,
		};

		try {
			const result = await submitRegistration(payload);
			if (result.success) {
				setSubmittedResult(result);
				return;
			}

			// Map server error codes to a top-of-form banner; field-level
			// errors paint inline.
			if (result.error === 'validation_failed' && result.fieldErrors != null) {
				setErrors(mapFieldErrors(result.fieldErrors));
				setBannerError(t('Please fix the highlighted fields and try again.'));
			} else if (result.error === 'rate_limited') {
				setBannerError(
					t(
						'Too many registration attempts from this network. Please wait a few minutes and try again.',
					),
				);
			} else if (result.error === 'registration_disabled') {
				setBannerError(
					t(
						'Registration is not currently accepted for this storefront. Please contact your sales representative.',
					),
				);
			} else if (result.error === 'origin_not_allowed') {
				setBannerError(
					t(
						'This page is not authorized to submit registrations to that storefront.',
					),
				);
			} else if (result.error === 'tenant_not_found') {
				setBannerError(
					t('We could not match your submission to a known storefront.'),
				);
			} else if (result.error === 'spam_rejected') {
				setBannerError(
					t('Your submission was flagged as automated. Please try again.'),
				);
			} else {
				setBannerError(
					result.message ??
						t('Something went wrong. Please try again in a moment.'),
				);
			}
		} catch (err) {
			setBannerError(
				err instanceof Error
					? err.message
					: t('Network error — please check your connection and retry.'),
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (submittedResult != null) {
		return (
			<div
				className={
					isModal
						? 'text-center py-6'
						: 'text-center py-12 px-4 max-w-md mx-auto'
				}
			>
				<div
					className={
						isModal
							? 'mx-auto w-12 h-12 rounded-full bg-portal-accent/20 flex items-center justify-center mb-4'
							: 'mx-auto w-16 h-16 rounded-full bg-portal-accent/15 flex items-center justify-center mb-5'
					}
				>
					<FontAwesomeIcon
						icon={faCheckCircle}
						className={
							isModal
								? 'text-2xl text-portal-accent'
								: 'text-3xl text-portal-accent'
						}
					/>
				</div>
				<h3
					className={
						isModal
							? 'text-base font-semibold text-white mb-2'
							: 'text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3'
					}
				>
					{t('Thanks — we got your information.')}
				</h3>
				<p
					className={
						isModal
							? 'text-sm text-white/75 mb-6'
							: 'text-base text-brand-muted mb-6'
					}
				>
					{t(
						'Our team will review your registration and reach out shortly to confirm your account and walk through next steps.',
					)}
				</p>
				{onComplete != null && (
					<button
						type="button"
						onClick={onComplete}
						className={
							isModal
								? 'inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-semibold shadow-lg shadow-black/30 transition-colors'
								: 'inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-semibold shadow-lg transition-colors'
						}
					>
						{t('Done')}
					</button>
				)}
			</div>
		);
	}

	// Two visual variants share the same form fields. Modal variant uses
	// the dark glass look (white text on dark) inherited from LoginModal;
	// page variant uses the standard light surface.
	const labelCls = isModal
		? 'block text-sm font-medium text-white/85 mb-1'
		: 'block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1';
	const inputBase = isModal
		? 'w-full px-3 py-2.5 rounded-lg bg-white border border-white/30 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-portal-accent focus:border-transparent shadow-sm'
		: 'w-full px-3 py-2.5 rounded-lg bg-brand-surface border border-brand-border text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-portal-accent focus:border-transparent';
	const errorCls = 'mt-1 text-xs text-red-400';

	const fieldClass = (field: keyof FormState): string =>
		errors[field] != null
			? `${inputBase} border-red-400 focus:ring-red-400`
			: inputBase;

	const regFieldClass = (fieldKey: string): string =>
		errors[fieldKey] != null
			? `${inputBase} border-red-400 focus:ring-red-400`
			: inputBase;

	// Resolved show/required state for a configurable standard field (PD-14370).
	const stdCfg = (key: string): StandardFieldState =>
		standardConfig[key] ??
		DEFAULT_STANDARD_CONFIG[key] ?? { isVisible: true, isRequired: false };

	// The required "*" marker, shown only when the field is configured required.
	const reqMark = (key: string): JSX.Element | null =>
		stdCfg(key).isRequired ? (
			<span className="text-portal-accent"> *</span>
		) : null;

	const checkboxLabelCls = isModal
		? 'flex items-center gap-2 text-sm text-white/85 cursor-pointer'
		: 'flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer';
	const helpCls = isModal
		? 'mt-1 text-xs text-white/55'
		: 'mt-1 text-xs text-brand-muted';

	// Render one admin-configured supplemental question by its type, matching
	// the storefront's styling. Standard fields stay hardcoded above; these are
	// the config-driven extras (PD-14331).
	const renderRegField = (field: PublicRegistrationFieldDto): JSX.Element => {
		const key = field.fieldKey;
		const value = regAnswers[key];
		const stringValue = typeof value === 'string' ? value : '';
		const fieldError = errors[key];
		const options = field.selectOptions ?? [];

		const labelNode = (
			<label htmlFor={`reg-cf-${key}`} className={labelCls}>
				{t(field.label)}
				{field.isRequired ? (
					<span className="text-portal-accent"> *</span>
				) : null}
			</label>
		);
		const helpNode =
			field.helpText != null && field.helpText.length > 0 ? (
				<p className={helpCls}>{t(field.helpText)}</p>
			) : null;
		const errorNode =
			fieldError != null ? <p className={errorCls}>{t(fieldError)}</p> : null;

		if (field.fieldType === 'textarea') {
			return (
				<div key={key}>
					{labelNode}
					<textarea
						id={`reg-cf-${key}`}
						rows={3}
						placeholder={
							field.placeholder != null ? t(field.placeholder) : undefined
						}
						value={stringValue}
						onChange={(e) => handleRegChange(key, e.target.value)}
						disabled={isSubmitting}
						className={regFieldClass(key)}
					/>
					{helpNode}
					{errorNode}
				</div>
			);
		}

		if (field.fieldType === 'select') {
			return (
				<div key={key}>
					{labelNode}
					<select
						id={`reg-cf-${key}`}
						value={stringValue}
						onChange={(e) => handleRegChange(key, e.target.value)}
						disabled={isSubmitting}
						className={regFieldClass(key)}
					>
						<option value="">{t('Select…')}</option>
						{options.map((option) => (
							<option key={option.value} value={option.value}>
								{t(option.label)}
							</option>
						))}
					</select>
					{helpNode}
					{errorNode}
				</div>
			);
		}

		if (field.fieldType === 'multiselect') {
			const selected = Array.isArray(value) ? (value as Array<string>) : [];
			return (
				<div key={key}>
					{labelNode}
					<div className="mt-1 space-y-1.5">
						{options.map((option) => (
							<label key={option.value} className={checkboxLabelCls}>
								<input
									type="checkbox"
									checked={selected.includes(option.value)}
									onChange={() => toggleMultiselect(key, option.value)}
									disabled={isSubmitting}
									className="accent-portal-accent"
								/>
								{t(option.label)}
							</label>
						))}
					</div>
					{helpNode}
					{errorNode}
				</div>
			);
		}

		if (field.fieldType === 'boolean') {
			return (
				<div key={key}>
					<label className={checkboxLabelCls}>
						<input
							type="checkbox"
							checked={value === true}
							onChange={(e) => handleRegChange(key, e.target.checked)}
							disabled={isSubmitting}
							className="accent-portal-accent"
						/>
						{t(field.label)}
						{field.isRequired ? (
							<span className="text-portal-accent"> *</span>
						) : null}
					</label>
					{helpNode}
					{errorNode}
				</div>
			);
		}

		const inputType =
			field.fieldType === 'number'
				? 'number'
				: field.fieldType === 'date'
					? 'date'
					: field.fieldType === 'datetime'
						? 'datetime-local'
						: field.fieldType === 'email'
							? 'email'
							: field.fieldType === 'phone'
								? 'tel'
								: field.fieldType === 'url'
									? 'url'
									: 'text';
		return (
			<div key={key}>
				{labelNode}
				<input
					id={`reg-cf-${key}`}
					type={inputType}
					placeholder={
						field.placeholder != null ? t(field.placeholder) : undefined
					}
					value={stringValue}
					onChange={(e) => handleRegChange(key, e.target.value)}
					disabled={isSubmitting}
					className={regFieldClass(key)}
				/>
				{helpNode}
				{errorNode}
			</div>
		);
	};

	return (
		<form
			onSubmit={handleSubmit}
			className={isModal ? 'space-y-3' : 'space-y-4'}
			noValidate
		>
			{bannerError != null && (
				<div
					className={
						isModal
							? 'flex items-start gap-2 bg-red-500/15 border border-red-400/30 text-red-200 text-xs rounded-lg px-3 py-2.5'
							: 'flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg px-3 py-2.5'
					}
				>
					<FontAwesomeIcon
						icon={faTriangleExclamation}
						className="mt-0.5 shrink-0"
					/>
					<span>{bannerError}</span>
				</div>
			)}

			{stdCfg('companyName').isVisible && (
				<div>
					<label htmlFor="reg-companyName" className={labelCls}>
						{t('Company Name')}
						{reqMark('companyName')}
					</label>
					<input
						id="reg-companyName"
						type="text"
						autoComplete="organization"
						required={stdCfg('companyName').isRequired}
						value={form.companyName}
						onChange={handleChange('companyName')}
						disabled={isSubmitting}
						className={fieldClass('companyName')}
					/>
					{errors.companyName != null && (
						<p className={errorCls}>{t(errors.companyName)}</p>
					)}
				</div>
			)}

			<div className="grid grid-cols-2 gap-3">
				<div>
					<label htmlFor="reg-firstName" className={labelCls}>
						{t('First Name')} <span className="text-portal-accent">*</span>
					</label>
					<input
						id="reg-firstName"
						type="text"
						autoComplete="given-name"
						required
						value={form.contactFirstName}
						onChange={handleChange('contactFirstName')}
						disabled={isSubmitting}
						className={fieldClass('contactFirstName')}
					/>
					{errors.contactFirstName != null && (
						<p className={errorCls}>{t(errors.contactFirstName)}</p>
					)}
				</div>
				<div>
					<label htmlFor="reg-lastName" className={labelCls}>
						{t('Last Name')} <span className="text-portal-accent">*</span>
					</label>
					<input
						id="reg-lastName"
						type="text"
						autoComplete="family-name"
						required
						value={form.contactLastName}
						onChange={handleChange('contactLastName')}
						disabled={isSubmitting}
						className={fieldClass('contactLastName')}
					/>
					{errors.contactLastName != null && (
						<p className={errorCls}>{t(errors.contactLastName)}</p>
					)}
				</div>
			</div>

			<div>
				<label htmlFor="reg-email" className={labelCls}>
					{t('Email')} <span className="text-portal-accent">*</span>
				</label>
				<input
					id="reg-email"
					type="email"
					autoComplete="email"
					required
					value={form.email}
					onChange={handleChange('email')}
					disabled={isSubmitting}
					className={fieldClass('email')}
				/>
				{errors.email != null && <p className={errorCls}>{t(errors.email)}</p>}
			</div>

			<div>
				<label htmlFor="reg-phone" className={labelCls}>
					{t('Phone')} <span className="text-portal-accent">*</span>
				</label>
				<input
					id="reg-phone"
					type="tel"
					autoComplete="tel"
					inputMode="tel"
					required
					value={form.phone}
					onChange={handleChange('phone')}
					disabled={isSubmitting}
					className={fieldClass('phone')}
				/>
				{errors.phone != null && <p className={errorCls}>{t(errors.phone)}</p>}
			</div>

			{stdCfg('address1').isVisible && (
				<div>
					<label htmlFor="reg-address1" className={labelCls}>
						{t('Street Address')}
						{reqMark('address1')}
					</label>
					<input
						id="reg-address1"
						type="text"
						autoComplete="address-line1"
						required={stdCfg('address1').isRequired}
						value={form.address1}
						onChange={handleChange('address1')}
						disabled={isSubmitting}
						className={fieldClass('address1')}
					/>
					{errors.address1 != null && (
						<p className={errorCls}>{t(errors.address1)}</p>
					)}
				</div>
			)}

			{stdCfg('address2').isVisible && (
				<div>
					<label htmlFor="reg-address2" className={labelCls}>
						{t('Address Line 2')}
						{reqMark('address2')}
					</label>
					<input
						id="reg-address2"
						type="text"
						autoComplete="address-line2"
						required={stdCfg('address2').isRequired}
						value={form.address2}
						onChange={handleChange('address2')}
						disabled={isSubmitting}
						className={fieldClass('address2')}
					/>
					{errors.address2 != null && (
						<p className={errorCls}>{t(errors.address2)}</p>
					)}
				</div>
			)}

			{/* City / State / ZIP — each cell is shown per the resolved standard
			    config (PD-14370). A 2-col grid gives the State dropdown a
			    readable width; hidden cells simply drop out. */}
			{(stdCfg('city').isVisible ||
				stdCfg('state').isVisible ||
				stdCfg('zip').isVisible) && (
				<div className="grid grid-cols-2 gap-3">
					{stdCfg('city').isVisible && (
						<div>
							<label htmlFor="reg-city" className={labelCls}>
								{t('City')}
								{reqMark('city')}
							</label>
							<input
								id="reg-city"
								type="text"
								autoComplete="address-level2"
								required={stdCfg('city').isRequired}
								value={form.city}
								onChange={handleChange('city')}
								disabled={isSubmitting}
								className={fieldClass('city')}
							/>
							{errors.city != null && (
								<p className={errorCls}>{t(errors.city)}</p>
							)}
						</div>
					)}
					{stdCfg('state').isVisible && (
						<div>
							<label htmlFor="reg-state" className={labelCls}>
								{t('State')}
								{reqMark('state')}
							</label>
							<select
								id="reg-state"
								autoComplete="address-level1"
								required={stdCfg('state').isRequired}
								value={form.state}
								onChange={handleChange('state')}
								disabled={isSubmitting}
								className={fieldClass('state')}
							>
								<option value="">{t('Select State')}</option>
								{US_STATES.map((usState) => (
									<option key={usState.value} value={usState.value}>
										{t(usState.label)}
									</option>
								))}
							</select>
							{errors.state != null && (
								<p className={errorCls}>{t(errors.state)}</p>
							)}
						</div>
					)}
					{stdCfg('zip').isVisible && (
						<div>
							<label htmlFor="reg-zip" className={labelCls}>
								{t('ZIP')}
								{reqMark('zip')}
							</label>
							<input
								id="reg-zip"
								type="text"
								autoComplete="postal-code"
								required={stdCfg('zip').isRequired}
								value={form.zip}
								onChange={handleChange('zip')}
								disabled={isSubmitting}
								className={fieldClass('zip')}
							/>
							{errors.zip != null && (
								<p className={errorCls}>{t(errors.zip)}</p>
							)}
						</div>
					)}
				</div>
			)}

			{stdCfg('businessType').isVisible && (
				<div>
					<label htmlFor="reg-businessType" className={labelCls}>
						{t('Business Type')}
						{reqMark('businessType')}
					</label>
					<input
						id="reg-businessType"
						type="text"
						placeholder={t('Restaurant, grocery, distributor, …')}
						required={stdCfg('businessType').isRequired}
						value={form.businessType}
						onChange={handleChange('businessType')}
						disabled={isSubmitting}
						className={fieldClass('businessType')}
					/>
					{errors.businessType != null && (
						<p className={errorCls}>{t(errors.businessType)}</p>
					)}
				</div>
			)}

			{stdCfg('leadSource').isVisible && (
				<div>
					<label htmlFor="reg-leadSource" className={labelCls}>
						{t('How did you hear about us?')}
						{reqMark('leadSource')}
					</label>
					<input
						id="reg-leadSource"
						type="text"
						placeholder={t('Trade show, referral, search, …')}
						required={stdCfg('leadSource').isRequired}
						value={form.leadSource}
						onChange={handleChange('leadSource')}
						disabled={isSubmitting}
						className={fieldClass('leadSource')}
					/>
					{errors.leadSource != null && (
						<p className={errorCls}>{t(errors.leadSource)}</p>
					)}
				</div>
			)}

			{regFields.length > 0 && (
				<div className={isModal ? 'space-y-3 pt-1' : 'space-y-4 pt-1'}>
					{regFields.map((field) => renderRegField(field))}
				</div>
			)}

			{stdCfg('notes').isVisible && (
				<div>
					<label htmlFor="reg-notes" className={labelCls}>
						{t('Anything else we should know?')}
						{reqMark('notes')}
					</label>
					<textarea
						id="reg-notes"
						rows={3}
						required={stdCfg('notes').isRequired}
						value={form.notes}
						onChange={handleChange('notes')}
						disabled={isSubmitting}
						className={fieldClass('notes')}
					/>
					{errors.notes != null && (
						<p className={errorCls}>{t(errors.notes)}</p>
					)}
				</div>
			)}

			{/*
			  Honeypot — visually hidden but present in the DOM. Bots
			  filling every input in sight will populate it; humans never
			  see it. Server rejects any submission with this set.
			  `aria-hidden` + `tabIndex={-1}` keep it off the screen reader
			  + tab path so legitimate accessibility tooling doesn't
			  accidentally fill it.
			*/}
			<div
				aria-hidden="true"
				style={{
					position: 'absolute',
					left: '-9999px',
					height: 0,
					width: 0,
					overflow: 'hidden',
				}}
			>
				<label htmlFor="reg-website-hp">{t('Website')}</label>
				<input
					id="reg-website-hp"
					type="text"
					tabIndex={-1}
					autoComplete="off"
					value={form.website}
					onChange={handleChange('website')}
				/>
			</div>

			<div
				className={
					isModal
						? 'flex items-center gap-2 pt-2'
						: 'flex items-center gap-3 pt-2'
				}
			>
				{onCancel != null && (
					<button
						type="button"
						onClick={onCancel}
						disabled={isSubmitting}
						className={
							isModal
								? 'flex-1 px-4 py-2.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors'
								: 'flex-1 px-4 py-3 rounded-full text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
						}
					>
						{t('Cancel')}
					</button>
				)}
				<button
					type="submit"
					disabled={isSubmitting}
					className={
						isModal
							? 'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-60 text-white text-sm font-semibold transition-colors min-h-touch'
							: 'flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-60 text-white text-sm font-semibold shadow-lg transition-colors min-h-touch'
					}
				>
					{isSubmitting && <FontAwesomeIcon icon={faSpinner} spin />}
					{isSubmitting ? t('Submitting…') : t('Submit Registration')}
				</button>
			</div>

			<p
				className={
					isModal
						? 'text-[11px] text-white/55 leading-relaxed'
						: 'text-xs text-brand-muted leading-relaxed'
				}
			>
				{t(
					'By submitting, you confirm that the contact details above are accurate. Our team will review your information and follow up to set up your account.',
				)}
			</p>
		</form>
	);
}
